'use client';

import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

function TooltipProvider({
  delayDuration = 300,
  skipDelayDuration = 150,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-sm text-balance',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };

// ─── TruncatedText ───────────────────────────────────────────────────────────
// Renders text with CSS truncation AND a tooltip — but only when the text is
// actually overflowing its container. When the text fits, no tooltip is shown,
// avoiding redundant hover noise on labels that are fully visible.
//
// Usage:
//   <TruncatedText className="max-w-[200px]">Personal Work</TruncatedText>
//
// Drives the Tooltip with controlled `open` state so the tree is always stable.
// Switching between a bare element and a Tooltip-wrapped element would cause
// React to remount the inner node — destroying the ref and making the tooltip
// require a double-mouseenter to appear.
//
// The overflow check is imperative on mouseenter (not a ResizeObserver) to keep
// the per-label footprint to a single ref with no continuous observers.

interface TruncatedTextProps {
  children: string;
  className?: string;
  tooltipSide?: React.ComponentProps<typeof TooltipContent>['side'];
}

function TruncatedText({ children, className, tooltipSide = 'top' }: TruncatedTextProps) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);

  const handleMouseEnter = () => {
    const el = ref.current;
    if (el) setOpen(el.scrollWidth > el.offsetWidth);
  };

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <span ref={ref} onMouseEnter={handleMouseEnter} className={cn('truncate', className)}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{children}</TooltipContent>
    </Tooltip>
  );
}

export { TruncatedText };
